'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { Button } from '~/app/_components/Button'
import { auth } from '~/components/FirebaseProvider'
import { signInWithPopup, GoogleAuthProvider, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, type User } from 'firebase/auth'
import { DefaultAvatar } from '~/components/DefaultAvatar'

type AuthProvider = 'google' | 'email'

interface AuthProviderStepProps {
  onProviderSelected: (
    provider: AuthProvider,
    firebaseUser: { uid: string; email: string | null; displayName: string | null; photoURL: string | null }
  ) => void
}

export function AuthProviderStep({ onProviderSelected }: AuthProviderStepProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [showEmailAuth, setShowEmailAuth] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user)
      setIsCheckingAuth(false)
    })

    return () => unsubscribe()
  }, [])

  const handleContinueAsUser = () => {
    if (currentUser) {
      onProviderSelected(
        currentUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email',
        {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName,
          photoURL: currentUser.photoURL ?? null,
        }
      )
    }
  }

  const handleChangeAccount = async () => {
    try {
      await signOut(auth)
      setCurrentUser(null)
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    try {
      const provider = new GoogleAuthProvider()
      provider.addScope('profile')
      provider.addScope('email')
      // Force account selection dialog to always show
      provider.setCustomParameters({
        prompt: 'select_account'
      })
      const result = await signInWithPopup(auth, provider)
      const user = result.user
      onProviderSelected('google', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
      })
    } catch (error) {
      console.error('Error signing in with Google:', error)
      setIsLoading(false)
    }
  }

  const handleEmailAuth = async () => {
    if (!email || !password) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Try to sign in first (user might already exist)
      try {
        const signInResult = await signInWithEmailAndPassword(auth, email, password)
        const user = signInResult.user
        onProviderSelected('email', {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName ?? null,
          photoURL: user.photoURL ?? null,
        })
        return
      } catch (signInError: unknown) {
        // If user doesn't exist, try to sign up
        // Note: auth/invalid-credential in newer Firebase can mean either wrong password or user not found
        const firebaseError = signInError as { code?: string; message?: string }
        const shouldTrySignUp = 
          firebaseError.code === 'auth/user-not-found' ||
          firebaseError.code === 'auth/invalid-credential'
        
        if (shouldTrySignUp) {
          try {
            const signUpResult = await createUserWithEmailAndPassword(auth, email, password)
            const user = signUpResult.user
            // We'll update displayName later in step 2 with first + last name
            onProviderSelected('email', {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName ?? null,
              photoURL: user.photoURL ?? null,
            })
            return
          } catch (signUpError: unknown) {
            // If sign up fails because email is already in use, it means the password was wrong
            const firebaseError = signUpError as { code?: string; message?: string }
            if (firebaseError.code === 'auth/email-already-in-use') {
              throw new Error('Incorrect password. Please try again.')
            }
            // For other sign up errors, show the error
            throw signUpError
          }
        }
        
        // For other sign-in errors (wrong password, etc.), throw to show error
        throw signInError
      }
    } catch (error: unknown) {
      console.error('Error with email auth:', error)
      
      // Map Firebase error codes to user-friendly messages
      let errorMessage = 'An error occurred. Please try again.'
      
      const firebaseError = error as { code?: string; message?: string }
      if (firebaseError?.code) {
        switch (firebaseError.code) {
          case 'auth/wrong-password':
            errorMessage = 'Incorrect password. Please try again.'
            break
          case 'auth/invalid-credential':
            // This can mean wrong password OR user not found
            // If we got here, it means we couldn't sign up either, so it's likely wrong password
            errorMessage = 'Incorrect email or password. Please check your credentials and try again.'
            break
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email. A new account will be created.'
            break
          case 'auth/email-already-in-use':
            errorMessage = 'This email is already registered. Please sign in with your password.'
            break
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.'
            break
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use a stronger password (at least 6 characters).'
            break
          case 'auth/too-many-requests':
            errorMessage = 'Too many failed attempts. Please try again later.'
            break
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your connection and try again.'
            break
          case 'auth/operation-not-allowed':
            errorMessage = 'Email/password authentication is not enabled. Please contact support.'
            break
          default:
            // For unknown errors, show a generic message but log the actual error
            errorMessage = firebaseError.message ?? 'An error occurred. Please try again.'
            console.error('Unhandled Firebase error:', firebaseError.code, firebaseError.message)
        }
      } else if (firebaseError?.message) {
        errorMessage = firebaseError.message
      }
      
      setError(errorMessage)
      setIsLoading(false)
    }
  }

  // Show loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-sage"></div>
      </div>
    )
  }

  // If user is already logged in, show continue as user UI
  if (currentUser && !showEmailAuth) {
    const displayName = currentUser.displayName ?? currentUser.email?.split('@')[0] ?? 'User'
    const avatarUrl = currentUser.photoURL

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <p className="text-gray-700 font-medium">
            Continue with your account
          </p>
        </div>

        <div className="space-y-4">
          <button
            onClick={handleContinueAsUser}
            disabled={isLoading}
            className="w-full py-4 text-lg font-semibold flex items-center gap-3 bg-white border-2 border-gray-200 hover:border-sage hover:bg-gray-50 rounded-full focus:outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-3 w-full px-4">
              {avatarUrl ? (
                <Image
                  src={avatarUrl}
                  alt={displayName}
                  width={40}
                  height={40}
                  className="rounded-full shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 shrink-0">
                  <DefaultAvatar className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 text-left min-w-0">
                <div className="font-semibold text-gray-900 truncate">
                  Continue as {displayName}
                </div>
                {currentUser.email && (
                  <div className="text-sm text-gray-500 font-normal truncate">
                    {currentUser.email}
                  </div>
                )}
              </div>
            </div>
          </button>

          <button
            onClick={handleChangeAccount}
            className="w-full text-sm text-gray-600 hover:text-gray-900 underline py-2 focus:outline-hidden cursor-pointer"
          >
            Change account
          </button>
        </div>
      </div>
    )
  }

  if (showEmailAuth) {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-sage focus:bg-white focus:outline-hidden focus:ring-sage sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-sage focus:bg-white focus:outline-hidden focus:ring-sage sm:text-sm"
          />
        </div>
        
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              setShowEmailAuth(false)
              setError(null)
            }}
            variant="outline"
            className="text-sm py-2 px-4"
            disabled={isLoading}
          >
            Back
          </Button>
          <Button
            onClick={handleEmailAuth}
            disabled={isLoading || !email || !password}
            className="flex-1 py-3"
            color="primary"
          >
            {isLoading ? 'Loading...' : 'Continue'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <p className="text-gray-700 font-medium">
          First, let&apos;s sign you in
        </p>
        <p className="text-sm text-gray-500">
          Choose your preferred sign-in method. We&apos;ll keep your account secure and your information private.
        </p>
      </div>
      
      <div className="space-y-4 mt-8">
        <Button
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="w-full py-4 text-lg font-semibold flex items-center justify-center gap-3"
          color="primary"
        >
          {isLoading ? (
            'Loading...'
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </>
          )}
        </Button>
        
        <Button
          onClick={() => setShowEmailAuth(true)}
          disabled={isLoading}
          className="w-full py-4 text-lg font-semibold flex items-center justify-center gap-3"
          color="primary"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Continue with Email
        </Button>
      </div>
    </div>
  )
}

